/**
 * My Bookings tab content: To fill + Submitted sections
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, IndianRupee, CircleCheckBig, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Loader, OTPInput } from '@/components/ui';
import type { BookingFormItem } from '@/stores/bookingFormsStore';
import { useBookingFormsStore } from '@/stores/bookingFormsStore';
import { Button } from '../../../components/ui/Button';
import apiClient from '@/api/client';
import { API_ENDPOINTS } from '@/api/endpoints';
import { storageManager } from '@/storage/storageManager';
import toast from 'react-hot-toast';
import { listChatThreads, normalizeBookingId } from '@/services/chat/chatApi';
import type { ChatThreadSummary } from '@/types/expressChat';

export interface MyBookingsTabContentProps {
  bookingFormsToFill: BookingFormItem[];
  bookingFormsSubmitted: BookingFormItem[];
  bookingsToFillLoading: boolean;
  bookingsSubmittedLoading: boolean;
  onOpenForm: (form: BookingFormItem, isSubmitted: boolean) => void;
  formatBookingEventDate: (dateStr: string | null | undefined) => string;
  formatBookingINR: (value: string | number | null | undefined) => string;
  ongoingEventDealId?: number | null;
  ongoingEventRemainingSeconds?: number;
  ongoingEventElapsedMinutes?: number;
  spotlightBookingDealId?: number | null;
  onSpotlightHandled?: () => void;
}

export const MyBookingsTabContent: React.FC<MyBookingsTabContentProps> = ({
  bookingFormsToFill,
  bookingFormsSubmitted,
  bookingsToFillLoading,
  bookingsSubmittedLoading,
  onOpenForm,
  formatBookingEventDate,
  formatBookingINR,
  ongoingEventDealId,
  spotlightBookingDealId,
  onSpotlightHandled,
}) => {
  const fetchBookingForms = useBookingFormsStore((s) => s.fetchBookingForms);
  const navigate = useNavigate();
  const [chatThreadsByBookingId, setChatThreadsByBookingId] = React.useState<
    Record<string, ChatThreadSummary>
  >({});
  const [startCodesByBookingId, setStartCodesByBookingId] = React.useState<Record<string, string>>({});
  const [verifyLoadingByBookingId, setVerifyLoadingByBookingId] = React.useState<Record<string, boolean>>({});
  const [otpVerifiedOverrideByBookingId, setOtpVerifiedOverrideByBookingId] = React.useState<
    Record<string, boolean>
  >({});
  const [highlightedBookingId, setHighlightedBookingId] = React.useState<string | null>(null);
  const submittedCardRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  React.useEffect(() => {
    let cancelled = false;
    void listChatThreads()
      .then((threads) => {
        if (cancelled) return;
        const next: Record<string, ChatThreadSummary> = {};
        for (const thread of threads) {
          next[normalizeBookingId(thread.booking_id)] = thread;
        }
        setChatThreadsByBookingId(next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const PERFORMANCE_TIMER_STORAGE_KEY = 'expressPerformanceTimer:v1';

  type PerformanceTimerState = {
    eventKey: string;
    dealId: number;
    reportingStartMs: number;
    endMs: number;
    durationMinutes: number;
    startedByUserAtMs: number;
    completedAtMs?: number;
    soundPlayed?: boolean;
    completionStatus?: 'playing' | 'done';
  };

  const [performanceStartModalOpen, setPerformanceStartModalOpen] = React.useState(false);
  const [performanceStartBookingId, setPerformanceStartBookingId] = React.useState<string | null>(null);
  const [performanceTimerState, setPerformanceTimerState] = React.useState<PerformanceTimerState | null>(null);
  const [performanceRemainingSeconds, setPerformanceRemainingSeconds] = React.useState<number | null>(null);
  const completionAudioInFlightRef = React.useRef(false);
  const lastRemainingSecondsRef = React.useRef<number | null>(null);
  const lastTimerEventKeyRef = React.useRef<string | null>(null);

  const formatMinutesSeconds = React.useCallback((totalSeconds: number): string => {
    const safe = Math.max(0, Math.floor(totalSeconds));
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${minutes}m : ${String(seconds).padStart(2, '0')}s`;
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      const stored = await storageManager.get<PerformanceTimerState>(PERFORMANCE_TIMER_STORAGE_KEY);
      if (cancelled) return;
      if (!stored) return;

      const isValid =
        typeof stored === 'object' &&
        stored != null &&
        typeof stored.eventKey === 'string' &&
        Number.isFinite(stored.dealId) &&
        Number.isFinite(stored.reportingStartMs) &&
        Number.isFinite(stored.endMs) &&
        Number.isFinite(stored.durationMinutes) &&
        Number.isFinite(stored.startedByUserAtMs);

      if (!isValid) {
        await storageManager.remove(PERFORMANCE_TIMER_STORAGE_KEY);
        return;
      }

      // Migration: old timers used `reportingStartMs` as the start anchor (countdown began at reporting time).
      // New requirement: countdown starts immediately on click. If we detect an old-shape state, convert it.
      const expectedDurationMs = Math.max(0, Math.floor(stored.durationMinutes)) * 60 * 1000;
      const hasOldAnchor =
        Number.isFinite(stored.reportingStartMs) &&
        stored.reportingStartMs > stored.startedByUserAtMs &&
        Math.abs((stored.endMs - stored.reportingStartMs) - expectedDurationMs) <= 1000;

      const migrated: PerformanceTimerState = hasOldAnchor
        ? {
            ...stored,
            reportingStartMs: stored.startedByUserAtMs,
            endMs: stored.startedByUserAtMs + expectedDurationMs,
          }
        : stored;

      if (hasOldAnchor) {
        await storageManager.set(PERFORMANCE_TIMER_STORAGE_KEY, migrated);
      }

      // If a saved timer is already expired, mark it done (never auto-play sound on load).
      const now = Date.now();
      if (Number.isFinite(migrated.endMs) && migrated.endMs <= now) {
        const done: PerformanceTimerState = {
          ...migrated,
          completedAtMs: migrated.completedAtMs ?? now,
          completionStatus: 'done',
          soundPlayed: true,
        };
        await storageManager.set(PERFORMANCE_TIMER_STORAGE_KEY, done);
        setPerformanceTimerState(done);
        return;
      }

      setPerformanceTimerState(migrated);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  React.useEffect(() => {
    if (!performanceTimerState) {
      setPerformanceRemainingSeconds(null);
      return;
    }

    const tick = () => {
      const now = Date.now();
      const remainingSeconds = Math.floor(Math.max(0, performanceTimerState.endMs - now) / 1000);
      setPerformanceRemainingSeconds(remainingSeconds);
    };

    tick();
    const intervalId = window.setInterval(tick, 1000);
    return () => window.clearInterval(intervalId);
  }, [performanceTimerState]);

  React.useEffect(() => {
    if (!performanceTimerState) return;
    if (performanceRemainingSeconds == null) return;

    // Only play completion sound when the countdown transitions from >0 to 0
    // for the same timer eventKey (prevents auto-play at start / on reload).
    if (lastTimerEventKeyRef.current !== performanceTimerState.eventKey) {
      lastTimerEventKeyRef.current = performanceTimerState.eventKey;
      lastRemainingSecondsRef.current = performanceRemainingSeconds;
      return;
    }

    const prevRemaining = lastRemainingSecondsRef.current;
    lastRemainingSecondsRef.current = performanceRemainingSeconds;

    const didJustFinish =
      prevRemaining != null && prevRemaining > 0 && performanceRemainingSeconds === 0;

    if (!didJustFinish) return;
    if (performanceTimerState.completionStatus === 'done') return;
    if (completionAudioInFlightRef.current) return;

    completionAudioInFlightRef.current = true;

    const playingState: PerformanceTimerState = {
      ...performanceTimerState,
      completedAtMs: performanceTimerState.completedAtMs ?? Date.now(),
      soundPlayed: true,
      completionStatus: 'playing',
    };

    setPerformanceTimerState(playingState);
    void storageManager.set(PERFORMANCE_TIMER_STORAGE_KEY, playingState);

    const finalizeDone = () => {
      completionAudioInFlightRef.current = false;
      setPerformanceTimerState((prev) => {
        if (!prev) return prev;
        const done: PerformanceTimerState = { ...prev, completionStatus: 'done' };
        void storageManager.set(PERFORMANCE_TIMER_STORAGE_KEY, done);
        return done;
      });
    };

    const playZingTenTimes = async () => {
      for (let i = 0; i < 10; i += 1) {
        const audio = new Audio('/zingSound.mp3');
        audio.preload = 'auto';

        const ended = new Promise<void>((resolve) => {
          audio.onended = () => resolve();
          audio.onerror = () => resolve();
        });

        try {
          const maybePromise = audio.play();
          if (maybePromise && typeof (maybePromise as Promise<void>).catch === 'function') {
            await (maybePromise as Promise<void>).catch(() => undefined);
          }
        } catch {
          break;
        }

        await ended;
      }
    };

    void (async () => {
      try {
        await playZingTenTimes();
      } finally {
        finalizeDone();
      }
    })();
  }, [performanceRemainingSeconds, performanceTimerState]);

  const setStartCodeFor = React.useCallback((bookingId: string, value: string) => {
    setStartCodesByBookingId((prev) => ({ ...prev, [bookingId]: value }));
  }, []);

  const stopCardOpenPropagation = React.useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  const shouldIgnoreCardOpen = React.useCallback((target: EventTarget | null) => {
    if (!(target instanceof Element)) return false;
    return Boolean(
      target.closest(
        'input, textarea, select, button, a, [role="button"], [data-stop-card-open="true"]'
      )
    );
  }, []);

  const verifyOtpForBooking = React.useCallback(async (bookingId: string) => {
    const otp = (startCodesByBookingId[String(bookingId)] ?? "").trim();
    if (!/^\d{4}$/.test(otp)) {
      toast.error("Please enter a valid 4-digit OTP.");
      return;
    }

    setVerifyLoadingByBookingId((prev) => ({ ...prev, [String(bookingId)]: true }));
    try {
      const verifyRes = await apiClient.post(
        API_ENDPOINTS.AUTH.JOBS_EXPRESS_BOOKING_VERIFY_OTP(bookingId),
        { otp },
      );
      if (verifyRes.status === 200) {
        await fetchBookingForms();
      }
      toast.success("OTP verified successfully.");
      setOtpVerifiedOverrideByBookingId((prev) => ({ ...prev, [String(bookingId)]: true }));
      setStartCodesByBookingId((prev) => ({ ...prev, [String(bookingId)]: "" }));

      // Open start-performance modal right after OTP verification.
      const verifiedForm = bookingFormsSubmitted.find((f) => String(f.id) === String(bookingId));
      const verifiedDealId = Number(verifiedForm?.deal_id || verifiedForm?.id || bookingId);
      const ongoingDealIdNum = Number(ongoingEventDealId ?? 0);
      // Always open modal after OTP verification. Start action itself is guarded to the backend-selected event.
      // (We still compute dealId here so future logic can use it if needed.)
      void verifiedDealId;
      void ongoingDealIdNum;
      setPerformanceStartBookingId(String(bookingId));
      setPerformanceStartModalOpen(true);
    } catch (err: any) {
      const data = err?.response?.data;

      // Handles shapes like:
      // { success:false, error:{ message:[{error_code:"..."},{message:"OTP not found..."}], details:{ message:"..." } } }
      const errorObj = data?.error;
      const detailsMessage =
        typeof errorObj?.details?.message === "string"
          ? errorObj.details.message
          : undefined;

      let extractedMessage: string | undefined;
      const nestedMessage = errorObj?.message;
      if (typeof nestedMessage === "string") {
        extractedMessage = nestedMessage;
      } else if (Array.isArray(nestedMessage)) {
        for (const item of nestedMessage) {
          if (item && typeof item === "object") {
            const maybe = (item as any).message;
            if (typeof maybe === "string" && maybe.trim()) {
              extractedMessage = maybe.trim();
              break;
            }
          } else if (typeof item === "string" && item.trim()) {
            extractedMessage = item.trim();
            break;
          }
        }
      }

      const apiMessage: string | undefined =
        detailsMessage ||
        extractedMessage ||
        (typeof data?.message === "string" ? data.message : undefined) ||
        (typeof err?.message === "string" ? err.message : undefined);

      toast.error(apiMessage || "Failed to verify OTP. Please try again.");
    } finally {
      setVerifyLoadingByBookingId((prev) => ({ ...prev, [String(bookingId)]: false }));
    }
  }, [fetchBookingForms, startCodesByBookingId, bookingFormsSubmitted, ongoingEventDealId]);

  const startMyPerformanceForBooking = React.useCallback(async (bookingId: string) => {
    const form = bookingFormsSubmitted.find((f) => String(f.id) === String(bookingId));
    if (!form) {
      toast.error('Unable to start performance. Booking not found.');
      return;
    }

    const dealId = Number(form.deal_id || form.id);
    const artistId = Number((form as any).artist_id);
    const durationMinutesRaw = (form as any).event_duration_mins ?? (form as any).performance_duration_mins;
    const durationMinutes = Number.isFinite(Number(durationMinutesRaw)) ? Math.max(0, Math.floor(Number(durationMinutesRaw))) : null;

    if (!Number.isFinite(dealId) || durationMinutes == null) {
      toast.error('Event timing details are missing. Please try again later.');
      return;
    }

    // Best-effort: API failure must not block local timer start.
    if (Number.isFinite(artistId)) {
      try {
        await apiClient.post(API_ENDPOINTS.AUTH.JOBS_EXPRESS_BOOKING_PERFORMANCE_START, {
          deal_id: dealId,
          artist_id: artistId,
        });
      } catch (err: any) {
        const data = err?.response?.data;
        const apiMessage: string | undefined =
          (typeof data?.error?.message === 'string' ? data.error.message : undefined) ||
          (typeof data?.message === 'string' ? data.message : undefined) ||
          (typeof err?.message === 'string' ? err.message : undefined);
        toast.error(apiMessage || 'Failed to start performance on server. Timer has started locally.');
      }
    } else {
      toast.error('Artist id missing. Timer has started locally.');
    }

    const expressBookingIdRaw = Number((form as any).express_booking_id ?? 0);
    const expressBookingId = Number.isFinite(expressBookingIdRaw) && expressBookingIdRaw > 0 ? expressBookingIdRaw : null;
    const eventKey = `express:${expressBookingId ?? `deal:${dealId}`}`;

    const startedByUserAtMs = Date.now();
    const reportingStartMs = startedByUserAtMs;
    const state: PerformanceTimerState = {
      eventKey,
      dealId,
      reportingStartMs,
      endMs: startedByUserAtMs + durationMinutes * 60 * 1000,
      durationMinutes,
      startedByUserAtMs,
      soundPlayed: false,
    };

    await storageManager.set(PERFORMANCE_TIMER_STORAGE_KEY, state);
    setPerformanceTimerState(state);
    // Ensure UI immediately shows full duration on click (e.g. 45m : 00s).
    const initialSeconds = durationMinutes * 60;
    lastTimerEventKeyRef.current = state.eventKey;
    lastRemainingSecondsRef.current = initialSeconds;
    setPerformanceRemainingSeconds(initialSeconds);
    setPerformanceStartModalOpen(false);
    setPerformanceStartBookingId(null);
  }, [bookingFormsSubmitted]);

  React.useEffect(() => {
    if (!spotlightBookingDealId || spotlightBookingDealId <= 0) return;
    const spotlightKey = String(spotlightBookingDealId);
    const targetNode = submittedCardRefs.current[spotlightKey];
    if (!targetNode) return;

    targetNode.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setHighlightedBookingId(spotlightKey);
    const timer = window.setTimeout(() => {
      setHighlightedBookingId((prev) => (prev === spotlightKey ? null : prev));
      onSpotlightHandled?.();
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [spotlightBookingDealId, onSpotlightHandled]);

  return (
    <div className="space-y-8">
      {/* To fill */}
      <section>
        <h3 className="text-sm font-bold text-[#8E8E93] uppercase tracking-wider mb-4">To fill</h3>
        {bookingsToFillLoading && bookingFormsToFill.length === 0 ? (
          <div className="rounded-2xl p-12 text-center flex items-center justify-center">
            <Loader size="small" />
          </div>
        ) : bookingFormsToFill.length === 0 ? (
          <div className="rounded-2xl border border-[#3A3A3C] bg-[#1C1C1E] p-8 text-center">
            <p className="text-[#8E8E93] text-sm">No booking forms pending. New requests will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {bookingFormsToFill.map((form, index) => (
              <motion.div
                key={form.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                onClick={() => onOpenForm(form, false)}
                className="cursor-pointer group relative overflow-hidden bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] p-5 transition-all hover:border-[#F33363]/40"
              >
                <div className="relative z-10">
                  <div className="flex justify-between items-start mb-4">
                    <span className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-widest">#{form.id}</span>
                    <span className="px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-500 text-[10px] font-bold uppercase tracking-wider border border-amber-500/20">
                      Action required
                    </span>
                  </div>
                  <h4 className="text-white font-bold text-lg mb-1 group-hover:text-[#F33363] transition-colors">{form.artist_act} · {form.event_type}</h4>
                  <p className="text-[#8E8E93] text-sm mb-4">{form.contact_name}</p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-xs text-[#8E8E93]">
                      <Calendar className="h-3 w-3" />
                      <span>{formatBookingEventDate(form.event_date)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#8E8E93]">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate">{form.venue_city_state || form.venue}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#8E8E93]">
                      <IndianRupee className="h-3 w-3" />
                      <span>{formatBookingINR(form.fee)}</span>
                    </div>
                  </div>

                  <div className="mt-5 flex items-center justify-end">
                    <Button
                      type="button"
                      variant="active"
                      className="h-9 px-4 rounded-xl text-xs font-bold bg-[#F33363] hover:bg-[#FF5E8E]"
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpenForm(form, false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#F33363]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Submitted */}
      <section>
        <h3 className="text-sm font-bold text-[#8E8E93] uppercase tracking-wider mb-4">Submitted</h3>
        {bookingsSubmittedLoading && bookingFormsSubmitted.length === 0 ? (
          <div className="rounded-2xl p-12 text-center flex items-center justify-center">
            <Loader size="small" />
          </div>
        ) : bookingFormsSubmitted.length === 0 ? (
          <div className="rounded-2xl border border-[#3A3A3C] bg-[#1C1C1E] p-8 text-center">
            <p className="text-[#8E8E93] text-sm">No submitted forms yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-6">
            {bookingFormsSubmitted.map((form, index) => (
              (() => {
                const bookingId = String(form.id);
                const isOtpVerified =
                  otpVerifiedOverrideByBookingId[bookingId] ?? (form.is_otp_verified === true);
                const showOtpBox = form.mode === 'express' && !isOtpVerified;
                const shouldSpotlight = highlightedBookingId === bookingId;
                const showPerformanceTimer =
                  performanceTimerState != null &&
                  Number.isFinite(Number(performanceTimerState.dealId)) &&
                  Number(performanceTimerState.dealId) === Number(form.deal_id || form.id);
                return (
                  <motion.div
                    key={form.id}
                    ref={(node) => {
                      submittedCardRefs.current[bookingId] = node;
                    }}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    onClick={(e) => {
                      if (shouldIgnoreCardOpen(e.target)) return;
                      onOpenForm(form, true);
                    }}
                    className={`cursor-pointer group relative overflow-visible lg:overflow-hidden bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] p-5 lg:p-4 xl:p-5 transition-all hover:border-[#F33363]/40 ${shouldSpotlight ? 'booking-card-spotlight border-[#F33363]/70 ring-2 ring-[#F33363]/40' : ''} ${form.mode === 'express'
                      ? '  w-full lg:max-w-[720px] xl:max-w-[760px] 2xl:max-w-[820px]'
                      : ''
                      } ${showOtpBox ? 'min-[480px]:grid  min-[480px]:gap-x-6' : ''}`}
                  >
                    <div className="flex flex-col justify-between items-center gap-4 w-full">
                      <div className="relative z-10 min-[480px]:col-start-1 min-[480px]:row-start-1 w-full">
                        <div className="relative mb-4">
                          <span className="text-[10px] font-bold text-[#8E8E93] uppercase tracking-widest">#{form.id}</span>

                          <div className="absolute right-0 top-0 flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2">
                              {form.mode === 'express' && (
                                <span className="px-2 py-0.5 rounded-full bg-[#F33363]/10 text-[#F33363] text-[10px] font-bold uppercase tracking-wider border border-[#F33363]/20">
                                  Express
                                </span>
                              )}
                              <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 text-[10px] font-bold uppercase tracking-wider border border-emerald-500/20">
                                Submitted
                              </span>
                            </div>

                            {showPerformanceTimer && (
                              performanceRemainingSeconds != null && performanceRemainingSeconds > 0 ? (
                                <div className="inline-flex items-center rounded-full bg-black/30 px-3.5 py-1.5 text-[12px] font-bold tracking-wider text-[#FFE3EC] border border-[#F33363]/25">
                                  {formatMinutesSeconds(performanceRemainingSeconds)}
                                </div>
                              ) : (
                                <div className="inline-flex items-center gap-2 rounded-full bg-black/30 px-3.5 py-1.5 text-[12px] font-bold tracking-wider text-emerald-300 border border-emerald-500/25">
                                  <CircleCheckBig className="h-4 w-4 text-emerald-400" />
                                  {performanceTimerState?.completionStatus === 'done' ? 'Completed' : null}
                                </div>
                              )
                            )}
                          </div>
                        </div>

                        <div className="mb-1 flex items-center justify-between gap-2">
                          <h4 className="min-w-0 truncate text-lg font-bold text-white transition-colors group-hover:text-[#F33363]">{form.event_type}</h4>
                          {form.mode === 'express' &&
                            chatThreadsByBookingId[normalizeBookingId(form.express_booking_code)] && (
                            <button
                              type="button"
                              data-stop-card-open="true"
                              onClick={(event) => {
                                event.stopPropagation();
                                const thread =
                                  chatThreadsByBookingId[
                                    normalizeBookingId(form.express_booking_code)
                                  ];
                                if (!thread) return;
                                navigate(`/jobs/chat/${encodeURIComponent(thread.booking_id)}`);
                              }}
                              className="chat-cta-jump ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#F33363]/70 bg-black/40 px-2.5 py-1 text-[11px] font-semibold text-white"
                            >
                              <MessageCircle className="h-3.5 w-3.5 text-[#F33363]" />
                              Chat with{' '}
                              <span className="text-[#F33363]">
                                {(
                                  chatThreadsByBookingId[
                                    normalizeBookingId(form.express_booking_code)
                                  ]?.client_full_name || 'Client'
                                )
                                  .trim()
                                  .split(/\s+/)[0]}
                              </span>
                            </button>
                          )}
                        </div>
                        <p className="text-[#8E8E93] text-sm mb-4">
                          {chatThreadsByBookingId[normalizeBookingId(form.express_booking_code)]
                            ?.client_full_name || form.contact_name}
                        </p>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2 text-xs text-[#8E8E93]">
                            <Calendar className="h-3 w-3" />
                            <span>{formatBookingEventDate(form.event_date)}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#8E8E93]">
                            <MapPin className="h-3 w-3" />
                            <span className="truncate">{form.venue_city_state || form.venue}</span>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-[#8E8E93]">
                            <IndianRupee className="h-3 w-3" />
                            <span>{formatBookingINR(form.fee)}</span>
                          </div>
                        </div>

                      </div>

                      {showOtpBox && (
                        <>
                          {/* START CODE box (desktop: fixed top-right in the card) */}
                          <div
                            className="mt-4 w-full ml-auto min-[480px]:mt-0 min-[480px]:ml-0 min-[480px]:col-start-2 min-[480px]:row-start-1 min-[480px]:self-start"
                            data-stop-card-open="true"
                            onClick={stopCardOpenPropagation}
                            onMouseDown={stopCardOpenPropagation}
                            onPointerDown={stopCardOpenPropagation}
                            onTouchStart={stopCardOpenPropagation}
                          >
                            <div className="rounded-2xl border border-[#F33363]/20 bg-[#0B0B0D]/70 p-3 sm:p-4 backdrop-blur-md shadow-[0_0_0_1px_rgba(243,51,99,0.15),0_16px_40px_rgba(0,0,0,0.45)]">
                              <p className="text-[11px] font-bold text-white text-center mb-1 tracking-wide">START CODE</p>
                              <OTPInput
                                length={4}
                                value={startCodesByBookingId[bookingId] ?? ''}
                                onChange={(value) => setStartCodeFor(bookingId, value)}
                                onComplete={(value) => setStartCodeFor(bookingId, value)}
                                className="flex gap-1 sm:gap-2 justify-center"
                                inputClassName="h-9 w-9 sm:h-11 sm:w-11 rounded-[0.8rem] text-base bg-black/60 border border-white/15 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]"
                              />
                              <p className="mt-2 text-[10px] leading-4 text-[#FF5E8E] text-center">
                                Start Code is mandatory to begin the Event
                              </p>
                            </div>
                          </div>

                          {/* START EVENT button (desktop: bottom-center in the card) */}
                          <div
                            className="mt-2 flex items-center justify-center min-[480px]:mt-4 min-[480px]:col-span-2 min-[480px]:row-start-2"
                            data-stop-card-open="true"
                            onClick={stopCardOpenPropagation}
                            onMouseDown={stopCardOpenPropagation}
                            onPointerDown={stopCardOpenPropagation}
                            onTouchStart={stopCardOpenPropagation}
                          >
                            <Button
                              type="button"
                              variant="active"
                              disabled={
                                verifyLoadingByBookingId[bookingId] === true ||
                                !/^\d{4}$/.test((startCodesByBookingId[bookingId] ?? '').trim())
                              }
                              className="h-10 w-[190px] rounded-full text-xs font-bold tracking-wide !bg-[#F33363]/15 !text-[#FF5E8E] border border-[#F33363]/30 hover:!bg-[#F33363]/20"
                              onClick={async (e) => {
                                e.stopPropagation();
                                await verifyOtpForBooking(bookingId);
                              }}
                            >
                              {verifyLoadingByBookingId[bookingId] === true ? "VERIFYING..." : "CONFIRM BOOKING"}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>

                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#F33363]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </motion.div>
                );
              })()
            ))}
          </div>
        )}
      </section>

      {/* Start My Booking modal (opens after OTP verification) */}
      {performanceStartModalOpen && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="rounded-2xl border border-[#3A3A3C]/50 shadow-xl w-full max-w-sm overflow-hidden bg-[linear-gradient(135deg,_#000000_0%,_#0c0c0e_40%,_#16161a_100%)]">
            <div className="p-6 space-y-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-1 min-w-0 flex-col items-center text-center">
                  <h3 className="text-sm font-semibold text-white">Hit the below button once</h3>
                  <p className="mt-1 text-sm font-semibold text-white">you're ready to begin!</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setPerformanceStartModalOpen(false);
                    setPerformanceStartBookingId(null);
                  }}
                  className="shrink-0 h-10 w-10 rounded-full border border-[#F33363]/40 text-white hover:bg-[#F33363]/10 transition-colors flex items-center justify-center"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <Button
                type="button"
                variant="active"
                className="w-full min-h-[48px] rounded-2xl font-bold text-sm bg-[#F33363] hover:bg-[#FF5E8E] text-white shadow-lg active:scale-[0.99]"
                onClick={() => {
                  const id = performanceStartBookingId;
                  if (!id) {
                    toast.error('Unable to start performance. Please try again.');
                    return;
                  }
                  void startMyPerformanceForBooking(id);
                }}
              >
                Start My Booking
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
